import {Component, OnDestroy, OnInit} from '@angular/core';
import {
    MxButtonModel,
    MxCustomizer,
    MxFormCustomizerEvent, MxInformationTooltipConfig,
    MxNotificationService,
    MxPreloaderService,
    OnBoFormReady,
    withPreloader
} from 'mx-core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {PrlValidationManagerService} from '../../../../shared/services/prl-validation-manager.service';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';
import {EMPTY, of, Subject} from 'rxjs';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {PrlForgotPasswordService} from '../../services/prl-forgot-password/prl-forgot-password.service';
import {PrlAuthenticationService} from '../../../../shared/services/prl-authentication.service';

@Component({
    selector: 'prl-change-password',
    templateUrl: './prl-change-password.component.html',
    standalone: false,
    styleUrls: ['prl-change-password.components.scss']
})
export class PrlChangePasswordComponent implements MxCustomizer<MxFormCustomizerEvent>, OnBoFormReady, OnInit, OnDestroy {
    submitButton: MxButtonModel;
    affiliate = '';

    resetPasswordConfig: MxInformationTooltipConfig = {
        links: [],
        header: 'Help',
        content: 'label.account.password.policy'
    };

    private destroy$$: Subject<null> = new Subject<null>();
    private form: FormGroup;
    private requestInProcess = false;
    private forceResetMode = false;

    constructor(
        private readonly forgotPasswordService: PrlForgotPasswordService,
        private readonly authService: PrlAuthenticationService,
        private readonly languageService: PrlLanguageService,
        private readonly notificationService: MxNotificationService,
        private readonly preloader: MxPreloaderService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly validationManagerService: PrlValidationManagerService
    ) {
        validationManagerService.init().subscribe();
    }

    ngOnInit() {
        this.submitButton = {
            label: () => this.getLabel('label.submit'),
            permission: 'all',
            tooltip: () => this.getLabel('label.send'),
            className: 'mxs-button--primary',
            disabled: () => !this.form || this.form.invalid || this.requestInProcess,
            clickHandler: this.updatePassword
        };

        const token = this.route.snapshot.params.token;
        this.forceResetMode = !token;

        if (this.forceResetMode) {
            if (!this.authService.isForceResetRequired()) {
                this.router.navigateByUrl('/login');
            }
            return;
        }

        this.forgotPasswordService
            .getUsernameByLink(token)
            .pipe(
                takeUntil(this.destroy$$),
                withPreloader(this.preloader),
                catchError(err => {
                    this.router.navigateByUrl('/login');
                    return EMPTY;
                })
            )
            .subscribe((response) => {
                this.affiliate = response;
            });
    }

    onCustomize(event: MxFormCustomizerEvent): void {
        event
            .customizeProps({
                addPrefixToTitle: false,
                showNavigation: false,
                showErrorOnSubmitInDialog: true,
                closeOnSubmit: true,
                informationKey: 'Change password'
            })
            .customizeActions({
                submit: {text: 'label.submit', visible: false},
                cancel: {text: 'label.cancel', visible: false}
            })
            .customizeFormMode('new')
            .customizeBoFormConfig({
                className: 'com.playtech.mexos.contract.account.UpdatePasswordPrlObj',
                force: true,
                formName: 'Change password'
            });
        event.addCustomizer(this);

    }


    boOnFormReady(form: FormGroup): void {
        this.form = form;
    }

    ngOnDestroy(): void {
        this.destroy$$.next(null);
    }

    private getLabel(label: string): string {
        return this.languageService.getLabel(label);
    }

    private updatePassword = () => {
        this.requestInProcess = true;

        const request = this.forceResetMode
            ? this.forgotPasswordService.forceChangePassword(this.form.controls.password.value)
            : this.forgotPasswordService.updatePassword(
                this.form.controls.password.value,
                this.route.snapshot.params.token,
                this.affiliate
            );

        request
            .pipe(
                switchMap(() => this.forceResetMode
                    ? this.authService.completeForceResetLogin()
                    : of(true)),
                catchError(err => {
                    this.requestInProcess = false;
                    this.notificationService.showFailure(err.error.detail);
                    return EMPTY;
                })
            )
            .subscribe({
                next: initialized => {
                    this.requestInProcess = false;
                    if (!initialized) {
                        return;
                    }
                    if (this.forceResetMode) {
                        this.notificationService.showSuccess('label.modify.account.change.password.message');
                        return;
                    }
                    this.router.navigateByUrl('/login');
                    this.notificationService.showSuccess('label.login.password.been.sent');
                }
            });
    };
}
